'use client'

import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { fetchDbNewsHistory, fetchDbNewsVocabulary } from '@/service/db-news.api'
import { snapshotAndUpdateQueryOptions } from '@/service/snapshot-query-options'
import type { DbNewsHistoryRequest, DbNewsPostList } from '@/types/generated/public_api'

export { useDbNewsStream } from '@/service/ws/use-db-news-stream'

export const dbNewsKeys = {
  all: ['db-news'] as const,
  history: (params: DbNewsHistoryRequest) => [...dbNewsKeys.all, 'history', params] as const,
  vocabulary: () => [...dbNewsKeys.all, 'vocabulary'] as const,
}

/**
 * DB News trigger-filter vocabulary for the trigger builder's suggestion dropdowns.
 * Cached for 5 minutes to match the backend's refresh cadence.
 */
export function useDbNewsVocabulary(enabled = true) {
  return useQuery({
    queryKey: dbNewsKeys.vocabulary(),
    queryFn: fetchDbNewsVocabulary,
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

export function useDbNewsHistory(params: DbNewsHistoryRequest = {}, enabled = true) {
  return useQuery({
    queryKey: dbNewsKeys.history(params),
    queryFn: () => fetchDbNewsHistory(params),
    enabled,
    ...snapshotAndUpdateQueryOptions,
  })
}

export function useDbNewsInfinite(params: DbNewsHistoryRequest = {}, enabled = true) {
  return useInfiniteQuery<DbNewsPostList>({
    queryKey: dbNewsKeys.history(params),
    queryFn: ({ pageParam = 0 }) =>
      fetchDbNewsHistory({
        ...params,
        offset: pageParam as number,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.posts.length < (params.limit ?? 50)) return undefined
      return allPages.length * (params.limit ?? 50)
    },
    initialPageParam: 0,
    enabled,
    // History under a live stream: fetched on mount, then the stream keeps it current. The
    // defaults (5s stale, refetch on focus) re-requested every loaded page, one by one.
    ...snapshotAndUpdateQueryOptions,
    ...snapshotAndUpdateQueryOptions,
  })
}
